import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, PlayCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * PostLoginShiftPrompt — one-shot cash-start prompt shown IMMEDIATELY
 * after a successful login when the operator has no open shift.
 *
 * The old ``ShiftGate`` fired only when the operator navigated to
 * /pos, /restaurant or /delivery — a waiter on mobile who lands
 * straight in ``MobileRestaurant`` would slip past it. This component
 * is mounted at the App root (both desktop Layout and MobileRestaurant
 * branches) and blocks the entire screen until the operator either:
 *
 *   1. enters a starting-cash amount (or 0) and opens the shift, or
 *   2. logs out.
 *
 * Once the shift is open, the flag ``needsShiftOpen`` in AuthContext
 * flips to false and this component unmounts itself.
 */
export default function PostLoginShiftPrompt() {
  const { user, needsShiftOpen, setShift, setNeedsShiftOpen, logout } = useAuth();
  const [cash, setCash] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !needsShiftOpen) return null;

  const open = async () => {
    setBusy(true);
    try {
      const r = await http.post("/shifts/open", { cash_start: parseFloat(cash) || 0 });
      setShift(r.data || null);
      setNeedsShiftOpen(false);
      toast.success("Άνοιξε η βάρδια");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία ανοίγματος βάρδιας");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4"
      data-testid="post-login-shift-prompt"
    >
      <div className="w-full max-w-[420px] rounded-2xl bg-white shadow-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0">
            <Lock size={22} />
          </div>
          <div>
            <div className="font-heading font-bold text-xl leading-tight">
              Άνοιγμα Βάρδιας
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Καλωσόρισες, {user.name}. Ξεκίνα εισάγοντας το αρχικό ταμείο.
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-amber-50 border-2 border-amber-200 p-3 text-sm text-amber-900">
          Δεν βρέθηκε ανοιχτή βάρδια για τον χειριστή σου. Καταχώρησε τα
          <strong> μετρητά έναρξης</strong> (μπορείς να αφήσεις 0€).
        </div>

        <div>
          <Label className="text-xs">Αρχικό Ταμείο (€)</Label>
          <Input
            type="number"
            step="0.01"
            value={cash}
            onChange={(e) => setCash(e.target.value)}
            placeholder="0.00"
            className="h-12 text-2xl font-mono tabular text-right font-bold border-2"
            autoFocus
            data-testid="post-login-shift-cash"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={open}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-bold"
            data-testid="post-login-shift-open"
          >
            <PlayCircle size={18} className="mr-2" />
            {busy ? "Άνοιγμα..." : "Άνοιγμα Βάρδιας & Είσοδος"}
          </Button>
          <Button
            variant="outline"
            onClick={() => logout()}
            data-testid="post-login-shift-cancel"
            className="h-10 text-slate-600"
          >
            Αποσύνδεση
          </Button>
        </div>
      </div>
    </div>
  );
}
